import { inject, Provider, signal } from '@angular/core';
import { FilePreviewApi, FilePreviewRequestDTO } from '../apis/file-preview.api';
import {
  FILE_PREVIEW_CONTEXT,
  FILE_PREVIEW_PROVIDER,
  FilePreviewContext,
  IFilePreviewProvider,
} from './file-preview-provider.interface';

/**
 * Implementazione FileSystem del provider di preview.
 * Usa FilePreviewApi per recuperare le anteprime dal controller FileSystem.
 *
 * In questo provider:
 * - `fileId` = path relativo del file nell'archivio (es. "cartella/sotto/file.pdf")
 * - `archiveId` e `isRelativePath` vengono letti dal token opzionale FILE_PREVIEW_CONTEXT
 *   (se fornito), oppure dai valori di default (stringa vuota e true).
 */
export class FileSystemPreviewProvider implements IFilePreviewProvider {
  #api = inject(FilePreviewApi);
  #context: FilePreviewContext | null = inject(FILE_PREVIEW_CONTEXT, { optional: true });

  #defaultArchiveId = signal('');
  #defaultIsRelativePath = signal(true);

  get #archiveId(): string {
    return this.#context?.archiveId() ?? this.#defaultArchiveId();
  }

  get #isRelativePath(): boolean {
    return this.#context?.isRelativePath() ?? this.#defaultIsRelativePath();
  }

  retrievePreview(fileType: string, fileId: string): Promise<any> {
    return this.#api.retrievePreview(fileType, this.#buildRequest(fileId));
  }

  retrieveTextContent(fileId: string): Promise<{ textContent: string }> {
    return this.#api.retrieveTextContent(this.#buildRequest(fileId));
  }

  downloadFile(fileId: string): Promise<Blob> {
    return this.#api.downloadFile(this.#buildRequest(fileId));
  }

  #buildRequest(fileId: string): FilePreviewRequestDTO {
    return {
      fileId,
      archiveId: this.#archiveId,
      isRelativePath: this.#isRelativePath,
    };
  }
}

/**
 * Provider da usare nei componenti FileSystem per fornire il FILE_PREVIEW_PROVIDER.
 * Esempio: providers: [FILE_SYSTEM_PREVIEW_PROVIDER]
 */
export const FILE_SYSTEM_PREVIEW_PROVIDER: Provider = {
  provide: FILE_PREVIEW_PROVIDER,
  useClass: FileSystemPreviewProvider,
};
